import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";
import { GetAdminUsersParams } from "features/adminUsers/adminUsers";
import { BackendUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";

interface BackendUserTableProps {
  users: BackendUser[];
  isLoading: boolean;
  isFetching: boolean;
  selectedUserId?: number;
  onManage: (user: BackendUser) => void;
  pagination?: ReactNode;
  sortBy: GetAdminUsersParams["sort_by"];
  sortOrder: GetAdminUsersParams["sort_order"];
  onSortChange: (field: GetAdminUsersParams["sort_by"]) => void;
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 7 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 1 ? 120 : "80%"} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export default function BackendUserTable({
  users,
  isLoading,
  isFetching,
  selectedUserId,
  onManage,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
}: BackendUserTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
        <Table stickyHeader size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Labels</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "folders"}
                  direction={sortBy === "folders" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("folders")}
                >
                  Folders
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "integrations"}
                  direction={sortBy === "integrations" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("integrations")}
                >
                  Integrations
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "sftp"}
                  direction={sortBy === "sftp" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("sftp")}
                >
                  SFTP
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "date_joined"}
                  direction={sortBy === "date_joined" ? sortOrder ?? "asc" : "asc"}
                  onClick={() => onSortChange("date_joined")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  selected={user.id === selectedUserId}
                  sx={{
                    opacity: isFetching ? 0.6 : 1,
                    "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.06)" },
                    "&.Mui-selected:hover": { bgcolor: "rgba(31,93,107,0.10)" },
                  }}
                >
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2" fontWeight={600}>
                        {user.client_name || user.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{user.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {user.folder_count > 0 && (
                        <Chip
                          label="Migration"
                          size="small"
                          sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                        />
                      )}
                      {user.integration_count > 0 && (
                        <Chip
                          label="Integration"
                          size="small"
                          sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600 }}
                        />
                      )}
                      {user.sftp_count > 0 && (
                        <Chip
                          label="SFTP"
                          size="small"
                          sx={{ bgcolor: "#e8eaf6", color: "#3949ab", fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.folder_count}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.integration_count}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.sftp_count}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.date_joined).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size={isMobile ? "medium" : "small"}
                      variant={user.id === selectedUserId ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => onManage(user)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        minHeight: isMobile ? 40 : undefined,
                        px: isMobile ? 2 : undefined,
                      }}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Box>
  );
}

